import { Activity, BarChart2, GitBranch, Layout, RefreshCw, Wrench } from "lucide-react";
import type { ReactNode } from "react";

type Feature = {
  icon: ReactNode;
  headline: string;
  body: string;
  badge?: string;
  // Contextual deep link. The homepage previously carried only header/footer
  // chrome links, so every interior page depended on nav alone for internal
  // PageRank. Anchor text is descriptive on purpose — never "learn more".
  href: string;
  linkLabel: string;
};

const FEATURES: Feature[] = [
  {
    icon: <GitBranch className="h-5 w-5" />,
    headline: "Multi-Agent Orchestration",
    body: "Transfer and delegate across named sub-agents with validated routing graphs and a per-hop audit trail. Every handoff is persisted atomically — no orphaned runs.",
    badge: "A2A ready",
    href: "/docs/runtime/transfer-vs-delegate",
    linkLabel: "How transfer and delegate routing works",
  },
  {
    icon: <Layout className="h-5 w-5" />,
    headline: "White-Label React UI",
    body: "Embed the full agent-management console inside your own product — themed to your brand, behind your auth and router, no iframe. A typed adapter wires it to your host app (routing, auth, transport). The companion chat SDK drops the end-user chat into any site.",
    href: "/chat-sdk",
    linkLabel: "Embeddable React AI chat widget",
  },
  {
    icon: <Wrench className="h-5 w-5" />,
    headline: "MCP + Tool Integration",
    body: "Connect any MCP server (SSE or Streamable HTTP) or define HTTP tools with per-property session injection. The LLM never sees tenant IDs — they're injected silently at call time.",
    badge: "MCP native",
    href: "/solutions/mcp-agent-platform",
    linkLabel: "Run MCP agents on a managed platform",
  },
  {
    icon: <Activity className="h-5 w-5" />,
    headline: "Triage-First Skill Routing",
    body: "The ReAct engine loads specialist skills on-demand via a virtual load_skill tool. No pre-flight LLM call — triage runs inside the loop with full context and zero extra cost.",
    href: "/docs/flows/skills",
    linkLabel: "Skill loading and triage docs",
  },
  {
    icon: <RefreshCw className="h-5 w-5" />,
    headline: "Streaming + Replay",
    body: "SSE streaming with monotonic chunk writes and a deduplicated replay endpoint. Reconnect mid-run from any index without triggering a duplicate execution.",
    href: "/docs/runtime/resume-streaming",
    linkLabel: "Resume and replay a streaming run",
  },
  {
    icon: <BarChart2 className="h-5 w-5" />,
    headline: "Full Observability",
    body: "Per-run OpenTelemetry spans, Prometheus-style metrics, and a step-by-step execution trace. Every tool call, every routing decision, every token — inspectable.",
    href: "/docs/runtime/routing-timeline",
    linkLabel: "Inspect the routing timeline",
  },
];

export default function Features() {
  return (
    <section id="features" className="py-20">
      <div className="mb-12">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
          Platform capabilities
        </p>
        <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
          Everything you'd build anyway.
          <br />
          Already built.
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-text-muted">
          Production-grade AI agent infrastructure — from the execution engine to the UI library to
          the streaming API — without the months of plumbing.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map((feature) => (
          <div
            key={feature.headline}
            className="group relative rounded-2xl border border-border bg-bg-secondary/60 p-6 transition-all duration-200 hover:border-primary-border hover:bg-bg-hover"
          >
            {feature.badge && (
              <span className="absolute right-4 top-4 rounded-full border border-primary-border bg-primary-dim px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-text">
                {feature.badge}
              </span>
            )}
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-bg text-primary transition-colors group-hover:border-primary-border group-hover:bg-primary-dim">
              {feature.icon}
            </div>
            <h3 className="mb-2 text-sm font-bold text-text">{feature.headline}</h3>
            <p className="text-sm leading-relaxed text-text-muted">{feature.body}</p>
            <a
              href={feature.href}
              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary-text underline-offset-4 hover:underline"
            >
              {feature.linkLabel}
              <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
